import * as signalR from '@microsoft/signalr';
import { CbsApiUrl } from './ExternalApi';
import store from '../store';

export interface NotificationCreatedV1 {
  messageId: string;
  notificationId: string;
  userId: string;
  websiteGuid: string | null;
  type: string;
  title: string;
  message: string;
  dataJson: string | null;
  severity: string | null;
  createdAtUtc: string;
  correlationId: string | null;
  causationId: string;
  contractVersion: 1;
}

export interface BookingUpdatedNotification {
  entityType: string;
  eventType: number;
  source: string;
  guid: string;
  hash: string | null;
  timestampUtc: string;
  environment: string;
  entityUpdatedAt: string;
}

type NotificationCreatedCallback = (event: NotificationCreatedV1) => void;
type BookingUpdatedCallback = (event: BookingUpdatedNotification) => void;
type ConnectionStateCallback = (connected: boolean) => void;

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY_MS = 5000;
const AUTOMATIC_RECONNECT_DELAYS_MS = [0, 2000, 10000, 30000];

function currentToken(): string | null {
  return store.getState().user?.token ?? null;
}

function currentUserId(): string | null {
  const userId = store.getState().user?.userId;
  return typeof userId === 'string' && userId.trim() ? userId.trim() : null;
}

function normalizeIdentity(value: string | null): string | null {
  return value ? value.trim().replace(/[{}]/g, '').toLowerCase() : null;
}

function currentIdentity(): string | null {
  const userId = normalizeIdentity(currentUserId());
  if (userId) return userId;
  const token = currentToken();
  return token ? `token:${token}` : null;
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function nullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function validIsoDate(value: unknown): value is string {
  return nonEmptyString(value) && !Number.isNaN(Date.parse(value));
}

export function parseSignalRPayload(payload: unknown): unknown | null {
  if (typeof payload !== 'string') return payload;
  try {
    return JSON.parse(payload);
  } catch (error) {
    console.warn('NotificationSignalR: ignoring malformed JSON payload', error);
    return null;
  }
}

export function isNotificationCreatedV1(payload: unknown): payload is NotificationCreatedV1 {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
  const event = payload as Partial<NotificationCreatedV1>;
  return event.contractVersion === 1
    && nonEmptyString(event.messageId)
    && nonEmptyString(event.notificationId)
    && nonEmptyString(event.userId)
    && nonEmptyString(event.type)
    && typeof event.title === 'string'
    && typeof event.message === 'string'
    && nullableString(event.websiteGuid)
    && nullableString(event.dataJson)
    && nullableString(event.severity)
    && validIsoDate(event.createdAtUtc)
    && nullableString(event.correlationId)
    && nonEmptyString(event.causationId);
}

export function isBookingUpdatedNotification(payload: unknown): payload is BookingUpdatedNotification {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
  const event = payload as Partial<BookingUpdatedNotification>;
  return nonEmptyString(event.entityType)
    && typeof event.eventType === 'number'
    && nonEmptyString(event.source)
    && nonEmptyString(event.guid)
    && nullableString(event.hash)
    && validIsoDate(event.timestampUtc)
    && nonEmptyString(event.environment)
    && validIsoDate(event.entityUpdatedAt);
}

function invokeSafely<T>(name: string, callback: (value: T) => void, value: T): void {
  try {
    callback(value);
  } catch (error) {
    console.error(`NotificationSignalR: ${name} listener failed`, error);
  }
}

export class NotificationSignalRConnection {
  private connection: signalR.HubConnection | null = null;
  private startPromise: Promise<void> | null = null;
  private connected = false;
  private intentionallyStopped = true;
  private connectionStateCallbacks: ConnectionStateCallback[] = [];
  private reconnectedCallbacks: (() => void)[] = [];
  private notificationCallbacks: NotificationCreatedCallback[] = [];
  private bookingCallbacks: BookingUpdatedCallback[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private identityKey: string | null = null;
  private unsubscribeStore: (() => void) | null = null;
  private browserRecoveryHandlersAttached = false;

  constructor() {
    this.unsubscribeStore = store.subscribe(() => this.handleIdentityChange());
    this.attachBrowserRecoveryHandlers();
  }

  private attachBrowserRecoveryHandlers(): void {
    if (this.browserRecoveryHandlersAttached || typeof window === 'undefined') return;
    window.addEventListener('online', this.handleBrowserRecovery);
    window.addEventListener('focus', this.handleBrowserRecovery);
    this.browserRecoveryHandlersAttached = true;
  }

  private handleBrowserRecovery = (): void => {
    if (this.intentionallyStopped || !currentToken() || this.identityKey !== currentIdentity()) return;
    if (this.connected || this.startPromise) return;
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();
    this.start().catch(error => {
      console.warn('NotificationSignalR: browser recovery attempt failed', error);
      if (!this.intentionallyStopped) this.scheduleReconnect();
    });
  };

  async start(): Promise<void> {
    const identity = currentIdentity();
    if (!currentToken() || !identity) {
      throw new Error('NotificationSignalR: missing authenticated user identity');
    }

    if (this.connected && this.identityKey === identity) return;
    if (this.startPromise) return this.startPromise;
    if (this.connection && this.identityKey !== identity) await this.stop();

    this.intentionallyStopped = false;
    this.clearReconnectTimer();
    this.startPromise = this.createAndStart(identity);
    try {
      await this.startPromise;
    } finally {
      this.startPromise = null;
    }
  }

  private async createAndStart(identity: string): Promise<void> {
    const connection = new signalR.HubConnectionBuilder()
      .configureLogging(signalR.LogLevel.None)
      .withUrl(`${CbsApiUrl()}/cbsNotificationsHub`, {
        withCredentials: false,
        accessTokenFactory: () => currentToken() || '',
      })
      .withAutomaticReconnect(AUTOMATIC_RECONNECT_DELAYS_MS)
      .build();

    this.connection = connection;
    this.identityKey = identity;
    this.bindConnectionHandlers(connection);

    try {
      await connection.start();
      if (this.identityKey !== currentIdentity() || !currentToken()) {
        await this.stopConnection(connection);
        throw new Error('NotificationSignalR: identity changed during connection startup');
      }
      this.reconnectAttempts = 0;
      this.connected = true;
      this.notifyConnectionState(true);
    } catch (error) {
      if (this.connection === connection) {
        this.connection = null;
        this.identityKey = null;
      }
      this.connected = false;
      this.notifyConnectionState(false);
      throw error;
    }
  }

  private bindConnectionHandlers(connection: signalR.HubConnection): void {
    connection.on('NotificationCreated.v1', payload => {
      const parsed = parseSignalRPayload(payload);
      if (!isNotificationCreatedV1(parsed)) {
        console.warn('NotificationSignalR: ignoring invalid NotificationCreated.v1 payload');
        return;
      }
      const authenticatedUserId = normalizeIdentity(currentUserId());
      if (!authenticatedUserId || normalizeIdentity(parsed.userId) !== authenticatedUserId) {
        console.warn('NotificationSignalR: ignoring notification for another user');
        return;
      }
      this.notificationCallbacks.slice().forEach(callback => invokeSafely('NotificationCreated.v1', callback, parsed));
    });

    connection.on('BookingUpdated', payload => {
      const parsed = parseSignalRPayload(payload);
      if (!isBookingUpdatedNotification(parsed)) {
        console.warn('NotificationSignalR: ignoring invalid BookingUpdated payload');
        return;
      }
      this.bookingCallbacks.slice().forEach(callback => invokeSafely('BookingUpdated', callback, parsed));
    });

    connection.onreconnecting(() => {
      this.connected = false;
      this.notifyConnectionState(false);
    });

    connection.onreconnected(() => {
      if (this.identityKey !== currentIdentity() || !currentToken()) {
        void this.stop();
        return;
      }
      this.reconnectAttempts = 0;
      this.connected = true;
      this.notifyConnectionState(true);
      this.reconnectedCallbacks.slice().forEach(callback => invokeSafely('reconnected', callback, undefined));
    });

    connection.onclose(() => {
      if (this.connection !== connection) return;
      this.connected = false;
      this.connection = null;
      this.notifyConnectionState(false);
      if (!this.intentionallyStopped && this.identityKey === currentIdentity() && currentToken()) {
        this.scheduleReconnect();
      }
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.intentionallyStopped || this.identityKey !== currentIdentity() || !currentToken()) return;
      this.start().catch(error => {
        console.warn('NotificationSignalR: reconnect attempt failed', error);
        if (!this.intentionallyStopped) this.scheduleReconnect();
      });
    }, RECONNECT_DELAY_MS);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private async stopConnection(connection: signalR.HubConnection): Promise<void> {
    try {
      await connection.stop();
    } catch (error) {
      console.warn('NotificationSignalR: connection stop failed', error);
    }
  }

  private handleIdentityChange(): void {
    if ((!this.connection && !this.reconnectTimer) || this.identityKey === currentIdentity()) return;
    this.intentionallyStopped = true;
    this.clearReconnectTimer();
    void this.stop();
  }

  async stop(): Promise<void> {
    this.intentionallyStopped = true;
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    const connection = this.connection;
    this.connection = null;
    this.identityKey = null;
    this.connected = false;
    if (connection) await this.stopConnection(connection);
    this.notifyConnectionState(false);
  }

  isActive(): boolean {
    return this.connected && this.connection !== null;
  }

  heartbeat(): Promise<void> {
    if (this.identityKey !== currentIdentity() || !currentToken()) {
      void this.stop();
      return Promise.resolve();
    }
    if (!this.connection || !this.connected) return Promise.resolve();
    return this.connection.invoke('Heartbeat').then(() => undefined);
  }

  onNotificationCreated(callback: NotificationCreatedCallback): () => void {
    if (!this.connection) throw new Error('NotificationSignalR: call start() before subscribing');
    this.notificationCallbacks.push(callback);
    return () => {
      this.notificationCallbacks = this.notificationCallbacks.filter(item => item !== callback);
    };
  }

  onBookingUpdated(callback: BookingUpdatedCallback): () => void {
    if (!this.connection) throw new Error('NotificationSignalR: call start() before subscribing');
    this.bookingCallbacks.push(callback);
    return () => {
      this.bookingCallbacks = this.bookingCallbacks.filter(item => item !== callback);
    };
  }

  onConnectionStateChange(callback: ConnectionStateCallback): () => void {
    this.connectionStateCallbacks.push(callback);
    invokeSafely('connection state', callback, this.connected);
    return () => {
      this.connectionStateCallbacks = this.connectionStateCallbacks.filter(item => item !== callback);
    };
  }

  onReconnected(callback: () => void): () => void {
    this.reconnectedCallbacks.push(callback);
    return () => {
      this.reconnectedCallbacks = this.reconnectedCallbacks.filter(item => item !== callback);
    };
  }

  private notifyConnectionState(connected: boolean): void {
    this.connectionStateCallbacks.slice().forEach(callback => invokeSafely('connection state', callback, connected));
  }

  dispose(): void {
    this.unsubscribeStore?.();
    this.unsubscribeStore = null;
    if (this.browserRecoveryHandlersAttached && typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleBrowserRecovery);
      window.removeEventListener('focus', this.handleBrowserRecovery);
      this.browserRecoveryHandlersAttached = false;
    }
    void this.stop();
    this.connectionStateCallbacks = [];
    this.reconnectedCallbacks = [];
    this.notificationCallbacks = [];
    this.bookingCallbacks = [];
  }
}

let globalConnection: NotificationSignalRConnection | null = null;

export function getNotificationSignalRConnection(): NotificationSignalRConnection {
  if (!globalConnection) globalConnection = new NotificationSignalRConnection();
  return globalConnection;
}
